import { useQuery } from "@tanstack/react-query";
import Services, { type TestCustomerOption, type TestSupplierOption } from "../services";
import {
  buildBillingDetailsFromCustomer,
  pickShipmentAddressFromCustomer,
  type TestPrefillConfig,
} from "../../PlaceOrderTest/testPrefillConfig";
import { DEFAULT_TESTER_CONFIG, type TesterConfig } from "./types";

export interface TesterEnvironment {
  mobileConfig: Record<string, string>;
  prefill: TestPrefillConfig | null;
  autonomousSupplierIds: string[];
  partConfirmationSupplierIds: string[];
  supplierLoginSecret: string;
  testSuppliers: TestSupplierOption[];
  defaultCustomer: TestCustomerOption | null;
  defaults: TesterConfig;
  warnings: string[];
}

export const applyCustomerToConfig = (
  config: TesterConfig,
  customer: TestCustomerOption,
  prefill?: TestPrefillConfig | null
): TesterConfig => {
  const shipmentAddress = pickShipmentAddressFromCustomer(
    {
      _id: customer._id,
      name: customer.name,
      mobileNumber: customer.mobileNumber,
      gstin: customer.gstin,
      addresses: customer.addresses as any,
      billingAddress: customer.billingAddress as any,
    },
    { addressId: config.shipmentAddressId ?? prefill?.shipmentAddressId, fallback: prefill?.shipmentAddress }
  );
  const billing = shipmentAddress
    ? buildBillingDetailsFromCustomer(
        {
          _id: customer._id,
          name: customer.name,
          mobileNumber: customer.mobileNumber,
          gstin: customer.gstin,
          addresses: customer.addresses as any,
          billingAddress: customer.billingAddress as any,
        },
        shipmentAddress,
        prefill?.billingDetails ?? null
      )
    : prefill?.billingDetails ?? null;
  return {
    ...config,
    customerId: customer._id,
    customerName: customer.name,
    customerCode: customer.code,
    customerMobile: customer.mobileNumber,
    isTestCustomer: customer.isTestCustomer,
    shipmentAddressId: shipmentAddress?._id ? String(shipmentAddress._id) : undefined,
    shipmentAddress: (shipmentAddress as Record<string, unknown>) ?? undefined,
    billingDetails: (billing as Record<string, unknown>) ?? undefined,
  };
};

export const applySupplierToConfig = (
  config: TesterConfig,
  supplier: TestSupplierOption
): TesterConfig => ({
  ...config,
  supplierId: supplier._id,
  supplierName: supplier.name,
  supplierMobile: supplier.mobileNumber,
  isTestSupplier: supplier.isTestSupplier,
});

const buildEnvironment = async (): Promise<TesterEnvironment> => {
  const warnings: string[] = [];
  const mobileConfig = await Services.getMobileConfig();
  const prefill = Services.getPrefillConfig(mobileConfig);
  if (!prefill) {
    warnings.push("orca_test_order_prefill_config is missing in mobile config; defaults are empty.");
  }
  const autonomousSupplierIds = Services.getAutonomousSupplierIds(mobileConfig);
  const partConfirmationSupplierIds = Services.getPartConfirmationEligibleSupplierIds(mobileConfig);
  const supplierLoginSecret = Services.getSupplierLoginSecret(mobileConfig);
  if (!supplierLoginSecret) {
    warnings.push(
      "supplier_login_secret is missing in mobile config; supplier proxy login cannot run."
    );
  }

  const [testSuppliers, defaultCustomer] = await Promise.all([
    Services.getTestSuppliers().catch(() => [] as TestSupplierOption[]),
    prefill?.customerId
      ? Services.getTestCustomerById(prefill.customerId).catch(() => null)
      : Promise.resolve(null),
  ]);
  if (prefill?.customerId && !defaultCustomer) {
    warnings.push(
      `Prefill customer ${prefill.customerId} was not found; select a customer.`
    );
  }
  if (!testSuppliers.length) {
    warnings.push("No suppliers flagged isTestSupplier=true were found; supplier steps cannot run.");
  }

  let defaults: TesterConfig = {
    ...DEFAULT_TESTER_CONFIG,
    remarks: prefill?.remarks ?? DEFAULT_TESTER_CONFIG.remarks,
    carMaker: prefill?.carMaker ?? "",
    parts: prefill?.partId
      ? [
          {
            partId: prefill.partId,
            partNumber: prefill.partNumber,
            partName: prefill.partName,
            quantity: 1,
          },
        ]
      : [],
  };

  if (prefill?.partId) {
    try {
      const resolved = await Services.resolvePart(prefill.partId, {
        partNumber: prefill.partNumber,
        partName: prefill.partName,
        carMaker: prefill.carMaker,
      });
      if (resolved) {
        defaults.parts = [
          {
            partId: prefill.partId,
            partNumber: resolved.partNumber || prefill.partNumber,
            partName: resolved.partName || prefill.partName,
            rootPartNumber: resolved.rootPartNumber,
            mrp: resolved.mrp,
            quantity: 1,
          },
        ];
      }
    } catch {
      // keep config-provided part details
    }
  }

  if (defaultCustomer) {
    defaults = applyCustomerToConfig(defaults, defaultCustomer, prefill);
  } else if (prefill?.shipmentAddress) {
    defaults.shipmentAddress = prefill.shipmentAddress as Record<string, unknown>;
    defaults.billingDetails = prefill.billingDetails as Record<string, unknown> | undefined;
  }

  const prefillSupplierId = (prefill as any)?.supplierId as string | undefined;
  const supplier =
    testSuppliers.find((s) => s._id === prefillSupplierId) ?? testSuppliers[0];
  if (supplier) defaults = applySupplierToConfig(defaults, supplier);

  return {
    mobileConfig,
    prefill,
    autonomousSupplierIds,
    partConfirmationSupplierIds,
    supplierLoginSecret,
    testSuppliers,
    defaultCustomer,
    defaults,
    warnings,
  };
};

export const useTesterEnvironment = () =>
  useQuery(["ORDER_FLOW_TESTER_ENV"], buildEnvironment, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
